<template>
  <div
    class="modal fade"
    id="custom-cue-dialog"
    tabindex="-1"
    aria-labelledby="customCueDialogLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="customCueDialogLabel">{{ dialogTitle }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" :aria-label="$t('common.close')"></button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="handleSubmit">
            <div class="mb-3">
              <label for="cue-type" class="form-label">{{ $t('shared.type_2') }}</label>
              <select v-model="typeId" class="form-select" id="cue-type" required>
                <option v-for="type in cueTypes" :key="type.id" :value="type.id">
                  {{ type.label }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label for="cue-title" class="form-label">
                {{ $t('plan_trail.custom_cue_dialog.hint') }}
                <span v-if="!requiresText" class="text-muted">{{ $t('plan_trail.custom_cue_dialog.optional') }}</span>
              </label>
              <input
                v-model="title"
                type="text"
                class="form-control"
                id="cue-title"
                :placeholder="placeholder"
                maxlength="255"
              />
              <small class="form-text text-muted">{{ helpText }}</small>
            </div>
          </form>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('common.cancel') }}</button>
          <button type="button" class="btn btn-primary" @click="handleSubmit" :disabled="!isValid">
            {{ $t('common.save') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch, PropType } from 'vue';
import { useStore } from 'vuex';
import { Modal } from 'bootstrap';
import { i18n } from '../../../i18n';
import { PLANNER_CUE_TYPES, cueTypeById, cueTypeForSign } from '../../../utils/models/plannerCues';
import { Translate, cueTypeLabel } from '../../../utils/models/plannerCueCopy';

const t: Translate = (key, named) => i18n.global.t(key, named ?? {});

/**
 * What the dialog is being opened on: a new cue at a control point, or a turn that already
 * exists somewhere in a segment's stored instructions.
 *
 * One target with two shapes rather than two props, so there is one watcher and one
 * initialiser — the add and edit paths cannot drift apart.
 */
export type CueDialogTarget =
  | { mode: 'add'; routeId: string | number; markerIndex: number }
  | {
      mode: 'edit';
      routeId: string | number;
      segmentIndex: number;
      entryIndex: number;
      /** The direction now in force — a person's override if they set one. */
      sign: number;
      /** Their own words only. Never the sentence the list composed from the sign. */
      text: string;
      /** Set when this is a hand-added cue; absent for a routed turn. */
      kind?: string;
      /** What the row currently reads, for the placeholder. */
      instruction: string;
    };

/**
 * Add a cue the router could not know about — a gate in a wall, a turn at a cairn, a water
 * tap. It is written into the stored turns of the segment that starts at the chosen control
 * point, so it reaches the hiker through the same pipeline a routed turn does: the directions
 * list, the recording banner and the spoken guidance all treat it as a maneuver.
 */
export default defineComponent({
  name: 'CustomCueDialog',
  props: {
    target: {
      type: Object as PropType<CueDialogTarget | null>,
      default: null
    }
  },
  setup(props) {
    const store = useStore();
    // Opens on the first turn type rather than on "General info": a cue attached to a control
    // point is usually a turn, and the one type that demands typing is a poor default.
    const typeId = ref('left');
    const title = ref('');

    const selectedType = computed(() => cueTypeById(typeId.value));

    const isEditMode = computed(() => props.target?.mode === 'edit');
    const dialogTitle = computed(() =>
      isEditMode.value
        ? i18n.global.t('plan_trail.custom_cue_dialog.edit_instruction')
        : i18n.global.t('plan_trail.custom_cue_dialog.add_turn_by_turn_prompt')
    );

    /** A routed turn cannot become a note — its `cue` key never changes. */
    const editingRoutedTurn = computed(
      () => props.target?.mode === 'edit' && props.target.kind === undefined
    );
    const cueTypes = computed(() =>
      (editingRoutedTurn.value ? PLANNER_CUE_TYPES.filter((type) => type.kind !== 'note') : PLANNER_CUE_TYPES).map(
        (type) => ({ ...type, label: cueTypeLabel(type.id, t) })
      )
    );

    // What a blank hint stores and the apps announce. Shown in the placeholder and
    // help text as-is, so they promise exactly what will be said.
    const spokenLabel = computed(() => selectedType.value?.label ?? '');

    /**
     * Whether the direction has actually been touched.
     *
     * Editing only the words must not write a `usign`, or every text edit would also claim a
     * direction the person never chose.
     */
    const initialTypeId = ref('left');
    const signChanged = computed(() => typeId.value !== initialTypeId.value);

    /**
     * Every turn type says something useful on its own — "Turn left" is a complete
     * instruction. General info says nothing without the planner's words, so it is the one
     * type that insists on them.
     */
    const requiresText = computed(() => selectedType.value?.kind === 'note');

    const placeholder = computed(() => {
      // Editing shows what the row currently reads, so a blank field plainly means "leave the
      // wording alone". Pre-filling it instead would freeze a machine-composed sentence into
      // `utext` — the one value that exists to mean "a person wrote this".
      if (props.target?.mode === 'edit' && props.target.text === '') return props.target.instruction;
      return requiresText.value
        ? i18n.global.t('plan_trail.custom_cue_dialog.what_should_the_hiker_know')
        : spokenLabel.value;
    });

    const helpText = computed(() =>
      requiresText.value
        ? i18n.global.t('plan_trail.custom_cue_dialog.read_out_as_written')
        : i18n.global.t('plan_trail.custom_cue_dialog.leave_blank_to_say', { label: spokenLabel.value })
    );

    const isValid = computed(() => {
      // A routed turn whose sign has no type of its own — a roundabout, a right-hand U-turn —
      // has nothing selected, and a text-only edit of it is still perfectly valid.
      if (isEditMode.value) return !requiresText.value || title.value.trim().length > 0;
      if (!selectedType.value) return false;
      return requiresText.value ? title.value.trim().length > 0 : true;
    });

    // Initialising on reopen rather than clearing on close, so a half-typed cue is not wiped
    // by a stray click outside the dialog.
    watch(
      () => props.target,
      (target) => {
        if (!target) return;
        if (target.mode === 'edit') {
          // `''` when the sign has no type of its own, which the leading option covers.
          typeId.value = cueTypeForSign(target.sign, target.kind === 'note')?.id ?? '';
          // Their words only — see `placeholder`.
          title.value = target.text;
        } else {
          // Opens on the first turn type rather than "General info": a cue attached to a
          // control point is usually a turn, and the one type that demands typing is a poor
          // default.
          typeId.value = 'left';
          title.value = '';
        }
        initialTypeId.value = typeId.value;
      }
    );

    const handleSubmit = () => {
      const target = props.target;
      if (!isValid.value || !target) return;

      if (target.mode === 'edit') {
        store.dispatch('planTrail/editInstruction', {
          routeId: target.routeId,
          segmentIndex: target.segmentIndex,
          entryIndex: target.entryIndex,
          // Blank clears the override and GraphHopper's own sentence comes back.
          text: title.value.trim(),
          // Only when the select was actually touched — otherwise a text-only edit would
          // also claim a direction nobody chose.
          sign: signChanged.value ? selectedType.value?.sign : undefined
        });
      } else {
        if (!selectedType.value) return;
        store.dispatch('planTrail/addCustomCue', {
          routeId: target.routeId,
          markerIndex: target.markerIndex,
          // An empty hint on a turn means the turn itself, stored as the cue type's
          // English label as it always was: the phones read this text out, to every
          // hiker who follows the route, whatever language the planner used.
          text: title.value.trim() || spokenLabel.value,
          sign: selectedType.value.sign,
          kind: selectedType.value.kind
        });
      }

      const element = document.getElementById('custom-cue-dialog');
      if (element) (Modal.getInstance(element) ?? new Modal(element)).hide();
    };

    return {
      cueTypes,
      typeId,
      title,
      requiresText,
      placeholder,
      helpText,
      isValid,
      handleSubmit,
      dialogTitle
    };
  }
});
</script>
